import React from 'react';
import { ButtonBase } from '@mui/material';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';
import UnfoldMoreRounded from '@mui/icons-material/UnfoldMoreRounded';

export type SortDirection = 'asc' | 'desc';

/**
 * Headings are upper-cased by the type scale, so every table reads the same
 * way. The one exception is a label that capitals would change: an
 * abbreviation with a lower-case ending (MQLs would read MQLS) or a word
 * cased inside (iPhone). Those are left exactly as written. Everything else —
 * "Best time (Eastern)", "Contact ID", "Total Legs" — is upper-cased.
 */
export function keepsItsCase(label: string): boolean {
  return /[A-Z]{2,}[a-z]|[a-z][A-Z]/.test(label);
}

export interface SortHeaderProps {
  label: string;
  /** Sorted by this column, and which way; null when not. */
  direction: SortDirection | null;
  onClick: () => void;
  /** A numeric column: the arrow sits before the label, which is right-aligned. */
  numeric?: boolean;
}

/**
 * A column heading you click to sort by, for a `DataTable` column's `header`.
 *
 * Every sortable table uses this, so a heading that sorts looks and answers
 * the same everywhere. Leave a heading plain where the row order is the
 * point — a report's rows in the report's order — since a heading that
 * reacts to the pointer promises a sort.
 *
 * The mark is always there: a faint two-way chevron while the column is not
 * sorted, so a reader can see what sorts without hunting with the pointer —
 * and nothing to see on a touch screen, where there is no hover at all. It
 * becomes a single arrow, in full ink, on the column actually sorted.
 *
 * On a numeric column the mark sits before the label, so the label's right
 * edge stays flush with the digits underneath it.
 */
export function SortHeader({ label, direction, onClick, numeric = false }: SortHeaderProps) {
  const Arrow = direction === 'desc' ? ArrowDownwardRounded : direction === 'asc' ? ArrowUpwardRounded : UnfoldMoreRounded;
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={`Sort by ${label}${direction === 'asc' ? ', sorted ascending' : direction === 'desc' ? ', sorted descending' : ''}`}
      sx={(t) => ({
        ...t.typography.overline,
        ...(keepsItsCase(label) && { textTransform: 'none' as const }),
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        flexDirection: numeric ? 'row-reverse' : 'row',
        color: direction ? t.tokens.text.primary : 'inherit',
        whiteSpace: 'normal',
        textAlign: numeric ? 'right' : 'left',
        borderRadius: `${t.ds.radius.xs}px`,
        '& svg': {
          opacity: direction ? 1 : 0.45,
          color: direction ? 'inherit' : t.tokens.text.tertiary,
          fontSize: t.typography.caption.fontSize,
        },
        '&:hover svg, &.Mui-focusVisible svg': { opacity: 1 },
        '&.Mui-focusVisible': { boxShadow: t.tokens.shadow.focusRing },
      })}
    >
      <span>{label}</span>
      <Arrow aria-hidden />
    </ButtonBase>
  );
}

/**
 * Next sort after a click on `key`: ascending, then descending, then none.
 *
 * `startDescending` turns it around, for a column where the first thing
 * anyone wants is the largest — a conversion rate, a spend, a count.
 */
export function nextSort<K extends string | number>(
  current: { key: K; direction: SortDirection } | null,
  key: K,
  { startDescending = false }: { startDescending?: boolean } = {},
): { key: K; direction: SortDirection } | null {
  const first: SortDirection = startDescending ? 'desc' : 'asc';
  const second: SortDirection = startDescending ? 'asc' : 'desc';
  if (!current || current.key !== key) return { key, direction: first };
  return current.direction === first ? { key, direction: second } : null;
}
